import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Star } from 'lucide-react';
import _ from 'lodash';

function getMousePosition(e: MouseEvent | React.MouseEvent) {
  return e.pageX;
}

interface StarRatingProps {
  canEdit?: boolean;
  handleRate?: (e: React.MouseEvent, rating: number) => void;
  fontSize?: string;
  name?: string;
  rating?: number;
}

const StarRating: React.FC<StarRatingProps> = ({
  canEdit = false,
  handleRate,
  fontSize = '1.2em',
  name,
  rating: propRating,
}) => {
  const starRatingRef = useRef<HTMLDivElement>(null);
  const [clientRect, setClientRect] = useState<DOMRect | null>(null);
  const [tempX, setTempX] = useState<number | null>(null);
  const [tempRating, setTempRating] = useState<number | null>(null);
  const [moving, setMoving] = useState(false);

  const getClientRect = useCallback(() => {
    const starRating = starRatingRef.current;
    const rect = starRating?.getBoundingClientRect();
    return rect || null;
  }, []);

  useEffect(() => {
    const handleResize = (e: Event) => {
      e.preventDefault();
      const gotClientRect = getClientRect();
      setClientRect(gotClientRect);
    };

    if (canEdit) {
      window.addEventListener('resize', handleResize);
      const gotClientRect = getClientRect();
      setClientRect(gotClientRect);
    }
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [canEdit, getClientRect]);

  const onClick = (e: React.MouseEvent) => {
    handleRate?.(e, tempRating || 0);
  };

  const onMove = (e: MouseEvent | React.MouseEvent, currentX: number) => {
    const rating = getRating(currentX);
    setTempX(currentX);
    setTempRating(rating);
    e.stopPropagation();
    e.preventDefault();
  };

  const onMouseMove = (e: MouseEvent) => {
    const currentX = getMousePosition(e);
    onMove(e, currentX);
  };

  const removeEvents = () => {
    document.removeEventListener('mousemove', onMouseMove);
  };

  const getRating = (currentX: number) => {
    const width = clientRect?.width || 0;
    const start = clientRect?.left || 0;
    return Math.ceil(((currentX - start) / width) * 5);
  };

  const onMouseEnter = (e: React.MouseEvent) => {
    document.addEventListener('mousemove', onMouseMove, false);
    const newTempX = getMousePosition(e);
    const newRating = getRating(tempX || 0);
    setTempX(newTempX);
    setTempRating(newRating);
    setMoving(true);
    e.stopPropagation();
    e.preventDefault();
  };

  const onMouseLeave = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    removeEvents();
    setMoving(false);
    setTempX(null);
    setTempRating(null);
  };

  let rating;
  if (moving) {
    rating = tempRating;
  } else {
    rating = propRating;
  }
  const range = _.range(5);
  const stars = range.map((num) => {
    const isFilled = Math.round((rating || 0) - num) > 0;
    const starClass = `h-5 w-5 ${isFilled ? 'text-beach' : 'text-light-gray'}`;
    const key = `star-rating-${name || 'default'}-${num}`;
    return (
      <Star
        key={key}
        className={starClass}
        fill={isFilled ? 'currentColor' : 'none'}
      />
    );
  });
  if (canEdit) {
    return (
      <div className="z-10">
        <div
          ref={starRatingRef}
          className="inline-flex items-center"
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          onClick={onClick}
        >
          {stars}
        </div>
      </div>
    );
  } else {
    return (
      <div className="z-10">
        <div className="flex items-center">{stars}</div>
      </div>
    );
  }
};

export default StarRating;
